<template>
  <div :player-state="playerState">
    <VideoPlayer
      ref="videoPlayerRef"
      class="video-player"
      v-if="videoSrc !== ''"
      :src="videoSrc"
      :streamingType="props.streamingInfo.type"
      @player-error="handlePlayerError"
      @player-start="handlePlayerStart"
      @player-ended="handlePlayerEnded"
      @player-unexpectedly-ended="handlePlayerUnexpectedlyEnded"
      @token-refresh="refreshToken"
      :force-reload-when-src-change="forceReloadWhenSrcChange"
    />
    <div class="overlay" v-if="showOverLay">
      <div class="content-box" v-if="!isStreamStarted">
        <img :src="startingSoonImagePath" alt="starting soon" @error="handleImageMissing" />
      </div>
      <div class="content-box" v-else-if="isStreamEnded">
        <img :src="endedImagePath" alt="ended" />
      </div>
      <div class="content-box" v-else>
        <i class="bi bi-exclamation-circle"></i>
        <div class="content-text">
          <p v-if="!isInternetConnected">{{ $t('streamingVideo.key018') }}</p>
          <p v-else-if="retryFetchVideoInterval !== null">{{ $t('streamingVideo.key019') }}</p>
          <p v-else>{{ $t('streamingVideo.key020') }}</p>
          <p v-if="retryFetchVideoInterval !== null" class="connection-loading">{{ $t('streamingVideo.key021') }}</p>
          <button v-else class="btn-reconnect" @click="handleReconnect">
            <i class="bi bi-arrow-counterclockwise"></i>
            {{ $t('streamingVideo.key022') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, onBeforeUnmount, onMounted, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import VideoPlayer from '@/components/VideoPlayer.vue';
import { getVideoPlayUrl, refreshVodToken } from '@/api/streamingService.js';
import { PlayerState } from '@/components/constants/videoPlayer.js';
import startingSoonImage from '@/assets/img/streaming/streaming_default_starting_soon.jpg';
import endedImage from '@/assets/img/streaming/streaming_default_ended.jpg';

// 12 * 5 = 1 minutes
const MAX_RETRY_TIMES = 12;

const RETRY_FETCH_VIDEO_INTERVAL = 5000;

const store = useStore();
const router = useRouter();

const videoSrc = ref('');
const videoPlayerRef = ref(null);
// first time need change src
const forceReloadWhenSrcChange = ref(true);
const isFetchingVideoSrc = ref(false);
const retryFetchVideoInterval = ref(null);
const retryTimes = ref(0);
const isFetchingVodToken = ref(false);

const isUnmounted = ref(false);
const isInternetConnected = ref(true);

const props = defineProps({
  /**
   * @description 節目資訊
   * @property {string} cid - 節目ID
   * @property {boolean} needLogin - 是否需要登入
   * @property {string} startTime - 節目開始時間
   * @property {string} endTime - 節目結束時間
   */
  streamingInfo: {
    type: Object,
    required: true,
  },
});

const playerState = computed(() => videoPlayerRef.value?.playerState);

const cloudFront = process.env.VUE_APP_CLOUD_FRONT;
const startingSoonImagePath = ref(`${cloudFront}/Image/Public/streaming/${props.streamingInfo.roomId}/StreamWebGL_DisplayTime.jpg`);
const endedImagePath = computed(() => endedImage);
const handleImageMissing = (e) => {
  e.target.src = startingSoonImage;
};

const isStreamStarted = computed(() => new Date(props.streamingInfo.startTime) <= new Date());
const isStreamEnded = computed(() => new Date(props.streamingInfo.endTime) <= new Date());

const showOverLay = computed(() => (playerState.value === PlayerState.Error && isStreamStarted.value) || !isStreamStarted.value || isStreamEnded.value);

let tokenRefreshInterval = null;

onMounted(async () => {
  try {
    await fetchVideoSrc();
  } catch (error) {
    console.error(error);
  }
});

onBeforeUnmount(() => {
  isUnmounted.value = true;

  removeRetryFetchVideoInterval();
  stopTokenRefreshTimer();
});

const logOut = () => {
  store.dispatch('logout');

  router.push({
    path: `/${store.state.currentLanguage}/member-login/login`,
  });
};

const handlePlayerError = async (error) => {
  console.error(error);

  if (error?.type === 'ErrorAuthorization') {
    logOut();
  } else if (error?.type !== 'error') {
    if (isStreamStarted.value) {
      setRetryFetchVideoInterval();
    }
  } else {
    console.error('error');
  }
};

// 觀測直播是否開始 開始時取得直播串流Url
watch(
  () => isStreamStarted.value,
  (newVal, oldVal) => {
    if (newVal && !oldVal) {
      fetchVideoSrc();
    }
  },
);

const fetchVideoSrc = async () => {
  try {
    if (isFetchingVideoSrc.value || isUnmounted.value) {
      return;
    }

    isFetchingVideoSrc.value = true;

    if (!isStreamStarted.value) {
      return;
    }

    const result = await getVideoPlayUrl(props.streamingInfo.roomId, props.streamingInfo.needLogin);
    let playUrl = result.data.data;
    // get playUrl before m3u8
    const newPlayUrlBeforeM3u8 = playUrl.split('.m3u8')[0];
    const oldPlayUrlBeforeM3u8 = videoSrc.value.split('.m3u8')[0];

    // video src origin changed
    if (newPlayUrlBeforeM3u8 !== oldPlayUrlBeforeM3u8) {
      forceReloadWhenSrcChange.value = true;
    }

    if (forceReloadWhenSrcChange.value) {
      // if url is the same, play video directly
      if (videoSrc.value === playUrl) {
        videoPlayerRef.value?.playVideo();
      } else {
        videoSrc.value = playUrl;
      }
    }
  } catch (error) {
    console.error(error);
    // first time fetch error, retry fetch video src
    setRetryFetchVideoInterval();
  } finally {
    isFetchingVideoSrc.value = false;
  }
};

const startTokenRefreshTimer = (expireTimestamp) => {
  stopTokenRefreshTimer();

  const expireDateMs = expireTimestamp * 1000;
  const now = Date.now();
  const timeUntilExpire = expireDateMs - now;

  // 緩衝時間：過期前 2 分鐘刷新
  const BUFFER_MS = 2 * 60 * 1000;
  let refreshDelay = timeUntilExpire - BUFFER_MS;

  tokenRefreshInterval = setTimeout(async () => {
    await refreshToken();
  }, refreshDelay);
};

const refreshToken = async () => {
  if (isFetchingVodToken.value) return;

  try {
    isFetchingVodToken.value = true;
    const res = await refreshVodToken(props.streamingInfo.roomId);
    const newExp = res.data?.data?.expiresAt;

    if (newExp) {
      startTokenRefreshTimer(newExp);
      videoPlayerRef.value?.wakeUpPlayer();
    } else {
      console.warn('刷新 API 未回傳新的過期時間');
    }
  } catch (err) {
    console.error(err);
  } finally {
    isFetchingVodToken.value = false;
  }
};

const stopTokenRefreshTimer = () => {
  if (tokenRefreshInterval) {
    clearTimeout(tokenRefreshInterval);
    tokenRefreshInterval = null;
  }
};

const setRetryFetchVideoInterval = () => {
  if (!retryFetchVideoInterval.value) {
    retryFetchVideoInterval.value = setInterval(retryFetchVideoSrc, RETRY_FETCH_VIDEO_INTERVAL);
  }

  forceReloadWhenSrcChange.value = true;
};

const removeRetryFetchVideoInterval = () => {
  if (retryFetchVideoInterval.value) {
    clearInterval(retryFetchVideoInterval.value);
    retryFetchVideoInterval.value = null;
  }

  retryTimes.value = 0;
};

const retryFetchVideoSrc = async (limit = true) => {
  if (retryTimes.value >= MAX_RETRY_TIMES) {
    removeRetryFetchVideoInterval();
    return;
  }

  await checkInternet();

  if (limit) {
    retryTimes.value++;
  }

  fetchVideoSrc();
};

const checkInternet = async () => {
  try {
    await fetch(`https://www.google.com/favicon.ico?_=${Date.now()}`, { mode: 'no-cors', cache: 'no-store' });
    isInternetConnected.value = true;
    return true;
  } catch (error) {
    isInternetConnected.value = false;
    return false;
  }
};

const handlePlayerStart = () => {
  removeRetryFetchVideoInterval();
  isInternetConnected.value = true;
  // if video is playing normally, don't need to change src
  forceReloadWhenSrcChange.value = false;
};

// when video ended, try to reload video src because the video might be unexpectedly interrupted
const handlePlayerEnded = () => {
  setRetryFetchVideoInterval();
};

const handlePlayerUnexpectedlyEnded = () => {
  if (isStreamEnded.value) {
    setRetryFetchVideoInterval();
  } else {
    if (!retryFetchVideoInterval.value) {
      retryFetchVideoInterval.value = setInterval(() => {
        retryFetchVideoSrc(false);
      }, RETRY_FETCH_VIDEO_INTERVAL);
    }

    forceReloadWhenSrcChange.value = true;
  }
};

const handleReconnect = () => {
  videoSrc.value = ''; // 清空資源重置播放器
  setRetryFetchVideoInterval();
};
</script>

<style lang="scss" scoped>
.overlay {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000000;
  .content-box {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    .btn-reconnect {
      border: 1px solid $primary;
      border-radius: 5px;
      padding: 5px 5px 3px;
      margin-top: 10px;
      font-size: 18px;
      background-color: rgba($primary, 0.2);
      cursor: pointer;
      transition: 0.3s;
      &:hover {
        background-color: rgba($primary, 0.4);
      }
    }
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      aspect-ratio: 16 / 9;
    }
    i.bi-exclamation-circle {
      font-size: 4rem;
      line-height: normal;
      margin-right: 20px;
    }
  }
}
</style>
